import { CurrencyPipe, DecimalPipe, PercentPipe } from '@angular/common';
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { PayrollJustificationDialogInput } from 'src/app/models/payroll-justification-breakdown.model';
import {
  PayrollsMileageDialogComponent,
  PayrollsMileageDialogData,
} from 'src/app/components/main/employees/rrhh/payrolls/payrolls-mileage-dialog/payrolls-mileage-dialog.component';
import { ApiService } from 'src/app/services/api.service';

/** @deprecated Use PayrollJustificationDialogInput */
export type PayrollPlusesJustificationDialogData = PayrollJustificationDialogInput;

const PAYROLL_PLUSES_POLICY_SLUG = 'payroll-pluses-policy';

@Component({
  selector: 'app-payroll-pluses-justification-dialog',
  templateUrl: './payroll-pluses-justification-dialog.component.html',
  styleUrls: ['./payroll-pluses-justification-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatTooltip,
    CurrencyPipe,
    PercentPipe,
    DecimalPipe,
  ],
})
export class PayrollPlusesJustificationDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<PayrollPlusesJustificationDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly currencyPipe = inject(CurrencyPipe);
  readonly data = inject<PayrollJustificationDialogInput>(MAT_DIALOG_DATA);

  openingWikiArticle = false;

  close(): void {
    this.dialogRef.close();
  }

  openPlusesPolicyArticle(): void {
    if (this.openingWikiArticle) {
      return;
    }

    this.openingWikiArticle = true;
    this.api.getWikiArticle(PAYROLL_PLUSES_POLICY_SLUG).subscribe({
      next: article => {
        const url = this.router.serializeUrl(
          this.router.createUrlTree(['/employees', 'wiki', article.wiki_node_id, 'article', article.id])
        );
        window.open(url, '_blank');
        this.openingWikiArticle = false;
      },
      error: () => {
        this.openingWikiArticle = false;
      },
    });
  }

  evaluationLabel(value: string | null): string {
    switch (value) {
      case 'good':
        return 'Bien';
      case 'medium':
        return 'Medio';
      case 'poor':
        return 'Mal';
      case 'not_calculable':
        return 'No calculable';
      default:
        return value ?? '—';
    }
  }

  evaluationShortLabel(value: string | null): string {
    switch (value) {
      case 'good':
        return 'B';
      case 'medium':
        return 'M';
      case 'poor':
        return 'X';
      case 'not_calculable':
        return '·';
      default:
        return '·';
    }
  }

  get kpiReductionPercent(): number | null {
    const factor = this.data.justification?.kpi_average_factor;
    if (factor == null) {
      return null;
    }
    return Math.round((1 - factor) * 100);
  }

  get kpiReductionAmount(): number | null {
    const justification = this.data.justification;
    if (justification?.bisual_plus_original_amount == null || justification?.bisual_plus_amount_after_kpi == null) {
      return null;
    }
    return Math.round((justification.bisual_plus_original_amount - justification.bisual_plus_amount_after_kpi) * 100) / 100;
  }

  get hasPlusesDetail(): boolean {
    const justification = this.data.justification;
    return !!justification && ((justification.pluses?.length ?? 0) > 0 || (justification.kpis?.length ?? 0) > 0);
  }

  get hasFuel(): boolean {
    return this.data.breakdown.total_fuel_cost > 0 || this.data.breakdown.distance_in_km > 0;
  }

  get hasExtra(): boolean {
    return (
      this.data.breakdown.additional_hours > 0 ||
      this.data.breakdown.additional_salary > 0 ||
      this.data.breakdown.additional_hours_amount > 0
    );
  }

  get hasAbsenceNotes(): boolean {
    return !!this.data.breakdown.comments?.trim() || !!this.data.breakdown.comment_for_employee?.trim();
  }

  signedAmount(amount: number): string {
    const absolute = this.currencyPipe.transform(Math.abs(amount)) ?? `${Math.abs(amount)}`;
    if (amount > 0) {
      return `+${absolute}`;
    }
    if (amount < 0) {
      return `−${absolute}`;
    }
    return absolute;
  }

  openMileageDetail(): void {
    if (!this.data.can_view_mileage_detail || this.data.payroll_id == null) {
      return;
    }

    this.dialog.open<PayrollsMileageDialogComponent, PayrollsMileageDialogData>(PayrollsMileageDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: {
        title: `Kilometraje · ${this.data.period_label ?? ''}`.trim(),
        payroll_id: this.data.payroll_id,
      },
    });
  }
}
